import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { useDispatch, useSelector } from 'react-redux';

import {
  getAttendContactAbsenceList,
  getAttendContactLateList,
  getAttendContactLeaveEarlyList,
  refreshAttendContactAbsenceList,
  refreshAttendContactLateList,
  refreshAttendContactLeaveEarlyList,
  removeAttendContactAbsence,
  removeAttendContactLate,
  removeAttendContactLeaveEarly,
} from '../thunk';

import DeleteAbsence from './DeleteAbsence';
import DeleteLate from './DeleteLate';
import DeleteLeaveEarly from './DeleteLeaveEarly';
import DetailAbsence from './DetailAbsence';
import DetailLate from './DetailLate';
import DetailLeaveEarly from './DetailLeaveEarly';
import {
  isEnableLoadingAbsenceSelector,
  isEnableLoadingLateSelector,
  isEnableLoadingLeaveEarlySelector,
} from './selector';

import type { TableBaseColumnType, TableBaseRowType } from '@/components/TableBase/Body';
import type { AttendContactAbsenceJson } from '@/types/domain/attendContact/entities/attendContactAbsenceEntity';
import type { AttendContactLateJson } from '@/types/domain/attendContact/entities/attendContactLateEntity';
import type { AttendContactLeaveEarlyJson } from '@/types/domain/attendContact/entities/attendContactLeaveEarlyEntity';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';

import AdditionalLoading from '@/components/AdditionalLoading';
import TableBase from '@/components/TableBase';
import { uiSlice } from '@/domain/ui/redux';
import { isPastDate, toStringDateDisplay, toStringTimeDisplay } from '@/utils/date';

/**
 * プロパティ
 */
export type Props = {
  /** 欠席リスト */
  absenceList: AttendContactAbsenceJson[],
  /** 遅刻リスト */
  lateList: AttendContactLateJson[],
  /** 早退リスト */
  leaveEarlyList: AttendContactLeaveEarlyJson[],
  /** 子ども */
  child: ChildJson,
};

const History: React.FC<Props> = ({
  absenceList,
  lateList,
  leaveEarlyList,
  child,
}) => {
  const dispatch = useDispatch();
  const isEnableLoadingAbsence = useSelector(isEnableLoadingAbsenceSelector);
  const isEnableLoadingLate = useSelector(isEnableLoadingLateSelector);
  const isEnableLoadingLeaveEarly = useSelector(isEnableLoadingLeaveEarlySelector);
  const [
    detailAbsenceData,
    setDetailAbsenceData,
  ] = React.useState<AttendContactAbsenceJson | undefined>(undefined);
  const [
    detailLateData,
    setDetailLateData,
  ] = React.useState<AttendContactLateJson | undefined>(undefined);
  const [
    detailLeaveEarlyData,
    setDetailLeaveEarlyData,
  ] = React.useState<AttendContactLeaveEarlyJson | undefined>(undefined);
  const [
    deleteAbsenceData,
    setDeleteAbsenceData,
  ] = React.useState<AttendContactAbsenceJson | undefined>(undefined);
  const [
    deleteLateData,
    setDeleteLateData,
  ] = React.useState<AttendContactLateJson | undefined>(undefined);
  const [
    deleteLeaveEarlyData,
    setDeleteLeaveEarlyData,
  ] = React.useState<AttendContactLeaveEarlyJson | undefined>(undefined);
  // 履歴
  const columns: TableBaseColumnType[] = [
    { id: 'category', label: '種別', minWidth: 20 },
    { id: 'date', label: '日', minWidth: 60 },
    { id: 'time', label: '時刻', minWidth: 60 },
  ];
  const rows = [];
  for (let i = 0; i < absenceList.length; i += 1) {
    if (absenceList[i].data.childId === child.childId) {
      rows.push({
        id: `absence-${i}`,
        category: '欠席',
        date: toStringDateDisplay(new Date(absenceList[i].data.absenceDate), true, true),
        time: '-',
      });
    }
  }
  for (let i = 0; i < lateList.length; i += 1) {
    if (lateList[i].data.childId === child.childId) {
      rows.push({
        id: `late-${i}`,
        category: '遅刻',
        date: toStringDateDisplay(new Date(lateList[i].data.lateDateTime), true, true),
        time: toStringTimeDisplay(new Date(lateList[i].data.lateDateTime), false),
      });
    }
  }
  for (let i = 0; i < leaveEarlyList.length; i += 1) {
    if (leaveEarlyList[i].data.childId === child.childId) {
      rows.push({
        id: `leaveearly-${i}`,
        category: '早退',
        date: toStringDateDisplay(new Date(leaveEarlyList[i].data.leaveEarlyDateTime), true, true),
        time: toStringTimeDisplay(new Date(leaveEarlyList[i].data.leaveEarlyDateTime), false),
      });
    }
  }
  if (rows.length === 0) {
    rows.push({
      id: '-',
      category: '-',
      date: '-',
      time: '登録されていません',
    });
  }
  rows.sort((a, b) => b.date.localeCompare(a.date));
  const actions = [
    {
      icon: <InfoIcon />,
      title: '詳細表示',
      action: (r: TableBaseRowType) => {
        const tmp = r.id.split('-');
        const index = parseInt(tmp[1], 10);
        if (tmp[0] === 'absence') {
          setDetailAbsenceData(absenceList[index]);
        } else if (tmp[0] === 'late') {
          setDetailLateData(lateList[index]);
        } else if (tmp[0] === 'leaveearly') {
          setDetailLeaveEarlyData(leaveEarlyList[index]);
        }
      },
    },
    {
      icon: <DeleteIcon />,
      title: '取り消し',
      action: (r: TableBaseRowType) => {
        const tmp = r.id.split('-');
        const index = parseInt(tmp[1], 10);
        if (tmp[0] === 'absence') {
          const item = absenceList[index];
          if (isPastDate(new Date(), new Date(item.data.absenceDate))) {
            dispatch(uiSlice.actions.setMessage({ key: 'warning', content: '過去の履歴を取り消しできません' }));
          } else {
            setDeleteAbsenceData(item);
          }
        } else if (tmp[0] === 'late') {
          const item = lateList[index];
          if (isPastDate(new Date(), new Date(item.data.lateDateTime))) {
            dispatch(uiSlice.actions.setMessage({ key: 'warning', content: '過去の履歴を取り消しできません' }));
          } else {
            setDeleteLateData(item);
          }
        } else if (tmp[0] === 'leaveearly') {
          const item = leaveEarlyList[index];
          if (isPastDate(new Date(), new Date(item.data.leaveEarlyDateTime))) {
            dispatch(uiSlice.actions.setMessage({ key: 'warning', content: '過去の履歴を取り消しできません' }));
          } else {
            setDeleteLeaveEarlyData(item);
          }
        }
      },
    },
  ];
  return (
    <div style={{ marginTop: '10px' }}>
      <TableBase
        rows={rows}
        columns={columns}
        actions={actions}
        refreshAction={() => {
          dispatch(refreshAttendContactAbsenceList());
          dispatch(refreshAttendContactLateList());
          dispatch(refreshAttendContactLeaveEarlyList());
        }}
        isPagination={false}
        isShowFilter={false}
        isShowFooter={false}
      />
      <AdditionalLoading
        isEnableLoading={isEnableLoadingAbsence || isEnableLoadingLate || isEnableLoadingLeaveEarly}
        onClick={() => {
          if (isEnableLoadingAbsence) {
            dispatch(getAttendContactAbsenceList());
          }
          if (isEnableLoadingLate) {
            dispatch(getAttendContactLateList());
          }
          if (isEnableLoadingLeaveEarly) {
            dispatch(getAttendContactLeaveEarlyList());
          }
        }}
      />
      <DetailAbsence
        data={detailAbsenceData}
        close={() => setDetailAbsenceData(undefined)}
      />
      <DetailLate
        data={detailLateData}
        close={() => setDetailLateData(undefined)}
      />
      <DetailLeaveEarly
        data={detailLeaveEarlyData}
        close={() => setDetailLeaveEarlyData(undefined)}
      />
      <DeleteAbsence
        data={deleteAbsenceData}
        close={(item) => {
          if (item) {
            dispatch(removeAttendContactAbsence(item));
          }
          setDeleteAbsenceData(undefined);
        }}
      />
      <DeleteLate
        data={deleteLateData}
        close={(item) => {
          if (item) {
            dispatch(removeAttendContactLate(item));
          }
          setDeleteLateData(undefined);
        }}
      />
      <DeleteLeaveEarly
        data={deleteLeaveEarlyData}
        close={(item) => {
          if (item) {
            dispatch(removeAttendContactLeaveEarly(item));
          }
          setDeleteLeaveEarlyData(undefined);
        }}
      />
    </div>
  );
};

export default History;
